import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  CheckSquare,
  Square,
  Search,
  Sparkles,
  Target,
  Filter,
  X,
  Layers,
  Loader2,
  MessageSquare,
  AlertTriangle,
  BookOpen,
  Ruler,
  ShieldCheck,
  Ban,
  Info,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  Hash,
  Database,
} from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api.config';
import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { DashboardSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { useToast } from '../../contexts/ToastContext';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { portfolioService } from '../../services/portfolio.service';
import TaxonomyCascade from '../../components/TaxonomyCascade';
import type { PdfAnalysisListItem, PdfAnalysisTopicSummary } from '../../types/pdf-analysis.types';

// ── Constants ───────────────────────────────────────────────────────────────

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'MCQ' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'case_study', label: 'Case Study' },
  { value: 'interactive', label: 'Interactive' },
];


// Map frontend question type value → API type param
const QUESTION_TYPE_API_MAP: Record<string, string> = {
  multiple_choice: 'mcq',
  short_answer: 'short_answer',
  case_study: 'case_study',
  interactive: 'interactive',
};

// Individual topic item within an OE code group
interface OeCodeSubItem {
  key: string; // unique id: oeCode::pdfId::idx
  topics: string;
  difficulty: 'easy' | 'medium' | 'hard';
  keywords: string[];
  pdfId: string;
  filename: string;
}

// OE code group: merges all items sharing the same oeCode across PDFs
interface OeCodeGroup {
  oeCode: string;
  topicName: string;
  difficulty: 'easy' | 'medium' | 'hard';
  keywords: string[];
  pdfSources: { pdfId: string; filename: string }[];
  itemCount: number;
  subItems: OeCodeSubItem[];
}

// ── Main Component ──────────────────────────────────────────────────────────

export function PdfQuestionGenerator() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // NQG-RQ-28: wizard step navigation (1 = Select PDFs, 2 = Topics, 3 = Generate)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Step 1: PDF selection
  const [selectedPdfIds, setSelectedPdfIds] = useState<Set<string>>(new Set());
  const [pdfSearch, setPdfSearch] = useState('');
  // NQG-RQ-31: portfolio-first drill-down (portfolio → Theme → Subtheme → Sub-subtheme)
  const [pdfPortfolioFilter, setPdfPortfolioFilter] = useState('all');
  const [pdfTaxonomyNode, setPdfTaxonomyNode] = useState<string | null>(null);
  // NQG-RQ-28: paginate the PDF list (configurable page size)
  const [pdfPage, setPdfPage] = useState(1);
  const PDF_PAGE_SIZE = 20;

  // Step 2: Topic selection (individual sub-item keys)
  const [selectedTopicKeys, setSelectedTopicKeys] = useState<Set<string>>(new Set());
  const [topicSearch, setTopicSearch] = useState('');
  const [topicDifficultyFilter, setTopicDifficultyFilter] = useState('all');

  // Step 3: Config
  const [generateTab, setGenerateTab] = useState<'settings' | 'custom'>('settings');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [difficultyDistribution, setDifficultyDistribution] = useState({ easy: 0, medium: 0, hard: 0 });
  const [difficultyPercentages, setDifficultyPercentages] = useState({ easy: 30, medium: 60, hard: 10 });
  const [count, setCount] = useState(5);
  const [mcqFormat, setMcqFormat] = useState<'anbima' | 'non-anbima'>('anbima');
  const [questionStyle, setQuestionStyle] = useState<'conceptual' | 'scenario' | 'mixed'>('conceptual');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [freeformPrompt, setFreeformPrompt] = useState('');

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
  // Async generation: live progress (X of Y accepted) while the job runs
  const [generationProgress, setGenerationProgress] = useState<{ completed: number; total: number } | null>(null);

  // When MCQ format is non-anbima, lock question type to MCQ
  useEffect(() => {
    if (mcqFormat === 'non-anbima') {
      setQuestionType('multiple_choice');
    }
  }, [mcqFormat]);

  // Auto-calculate difficulty distribution from percentages
  useEffect(() => {
    const easyCount = Math.round((difficultyPercentages.easy / 100) * count);
    const mediumCount = Math.round((difficultyPercentages.medium / 100) * count);
    const hardCount = Math.round((difficultyPercentages.hard / 100) * count);

    // Adjust to ensure total equals count
    let total = easyCount + mediumCount + hardCount;
    let adjusted = { easy: easyCount, medium: mediumCount, hard: hardCount };

    if (total !== count) {
      const diff = count - total;
      // Adjust the largest category
      if (adjusted.medium >= adjusted.easy && adjusted.medium >= adjusted.hard) {
        adjusted.medium += diff;
      } else if (adjusted.easy >= adjusted.hard) {
        adjusted.easy += diff;
      } else {
        adjusted.hard += diff;
      }
    }

    setDifficultyDistribution(adjusted);
  }, [count, difficultyPercentages]);

  // ── Fetch all PDF analyses (list) ──

  const { data: allAnalyses = [], isLoading: isLoadingList } = useQuery({
    queryKey: ['pdf-analyses'],
    queryFn: () => pdfAnalysisService.getAnalyses(),
  });

  // NQG-RQ-31: portfolios for the drill-down (cascade needs the selected one's id)
  const { data: generatorPortfolios = [] } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const selectedPortfolioId = useMemo(
    () => generatorPortfolios.find((p) => p.name === pdfPortfolioFilter)?._id || null,
    [generatorPortfolios, pdfPortfolioFilter]
  );

  // Only show completed analyses for selection
  const completedAnalyses = useMemo(
    () => allAnalyses.filter((a) => (a.status || 'completed') === 'completed'),
    [allAnalyses]
  );

  // NQG-RQ-26 v2: analyses without identified topics cannot be used for generation
  const hasNoTopics = (a: PdfAnalysisListItem) => a.totalItems === 0;

  // Filter PDFs: search + NQG-RQ-31 portfolio/taxonomy drill-down. A taxonomy
  // node matches every PDF placed at or UNDER it (pathIds carry ancestors).
  const filteredPdfs = useMemo(() => {
    let list = completedAnalyses;
    if (pdfPortfolioFilter !== 'all') {
      // The default portfolio implicitly contains PDFs with no assignment
      const isDefault = generatorPortfolios.find((p) => p.name === pdfPortfolioFilter)?.isDefault;
      list = list.filter(
        (a) =>
          (a.portfolios || []).includes(pdfPortfolioFilter) ||
          (isDefault && (a.portfolios || []).length === 0)
      );
    }
    if (pdfTaxonomyNode) {
      list = list.filter((a) =>
        (a.taxonomyPlacements || []).some((tp) => (tp.pathIds || []).includes(pdfTaxonomyNode))
      );
    }
    if (!pdfSearch.trim()) return list;
    const searchLower = pdfSearch.toLowerCase();
    return list.filter((analysis) => analysis.filename.toLowerCase().includes(searchLower));
  }, [completedAnalyses, pdfSearch, pdfPortfolioFilter, pdfTaxonomyNode, generatorPortfolios]);

  // NQG-RQ-28: page slice of the filtered PDF list
  const pdfTotalPages = Math.max(1, Math.ceil(filteredPdfs.length / PDF_PAGE_SIZE));
  const pagedPdfs = useMemo(
    () => filteredPdfs.slice((pdfPage - 1) * PDF_PAGE_SIZE, pdfPage * PDF_PAGE_SIZE),
    [filteredPdfs, pdfPage]
  );
  // Reset to first page whenever the search/filters change or the page overflows
  useEffect(() => {
    setPdfPage(1);
  }, [pdfSearch, pdfPortfolioFilter, pdfTaxonomyNode]);
  // Changing portfolio invalidates the taxonomy selection (per-portfolio tree)
  useEffect(() => {
    setPdfTaxonomyNode(null);
  }, [pdfPortfolioFilter]);
  useEffect(() => {
    if (pdfPage > pdfTotalPages) setPdfPage(pdfTotalPages);
  }, [pdfTotalPages, pdfPage]);

  // ── Fetch lightweight topic data for selected PDFs (batch) ──

  const selectedPdfIdsArray = useMemo(() => Array.from(selectedPdfIds), [selectedPdfIds]);

  const { data: pdfTopicsRecord = {} as Record<string, PdfAnalysisTopicSummary>, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['pdf-analysis-topics', selectedPdfIdsArray],
    queryFn: async () => {
      const results = await pdfAnalysisService.getAnalysesTopics(selectedPdfIdsArray);
      const record: Record<string, PdfAnalysisTopicSummary> = {};
      results.forEach((r) => {
        if (r?._id) record[r._id] = r;
      });
      return record;
    },
    enabled: selectedPdfIdsArray.length > 0,
  });

  // ── Fetch question type validation config ──

  const apiTypeParam = QUESTION_TYPE_API_MAP[questionType] || 'mcq';

  const { data: typeConfig, isLoading: isLoadingTypeConfig } = useQuery({
    queryKey: ['question-type-config', apiTypeParam],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/question-validation-config/types/${apiTypeParam}`);
      return res.data.data;
    },
    staleTime: 1000 * 60 * 30, // cache 30 min — config rarely changes
  });

  // ── Derived state ──

  const selectedPdfListItems = useMemo(
    () => completedAnalyses.filter((a) => selectedPdfIds.has(a._id)),
    [completedAnalyses, selectedPdfIds]
  );

  // Build OE code groups: merge items with same oeCode across all selected PDFs
  const allOeCodeGroups: OeCodeGroup[] = useMemo(() => {
    const map = new Map<string, OeCodeGroup>();
    for (const pdfId of selectedPdfIdsArray) {
      const summary = pdfTopicsRecord[pdfId];
      if (!summary?.items) continue;
      for (let i = 0; i < summary.items.length; i++) {
        const item = summary.items[i];
        const subItem: OeCodeSubItem = {
          key: `${item.oeCode}::${pdfId}::${i}`,
          topics: item.topics,
          difficulty: item.difficulty,
          keywords: [...item.keywords],
          pdfId,
          filename: summary.filename,
        };
        const existing = map.get(item.oeCode);
        if (existing) {
          existing.itemCount++;
          existing.subItems.push(subItem);
          // Merge unique keywords
          for (const kw of item.keywords) {
            if (!existing.keywords.includes(kw)) existing.keywords.push(kw);
          }
          // Add PDF source if not already listed
          if (!existing.pdfSources.some((s) => s.pdfId === pdfId)) {
            existing.pdfSources.push({ pdfId, filename: summary.filename });
          }
        } else {
          map.set(item.oeCode, {
            oeCode: item.oeCode,
            topicName: item.topics,
            difficulty: item.difficulty,
            keywords: [...item.keywords],
            pdfSources: [{ pdfId, filename: summary.filename }],
            itemCount: 1,
            subItems: [subItem],
          });
        }
      }
    }
    return Array.from(map.values());
  }, [selectedPdfIdsArray, pdfTopicsRecord]);

  // Total individual topics across all groups
  const totalTopicCount = useMemo(
    () => allOeCodeGroups.reduce((sum, g) => sum + g.itemCount, 0),
    [allOeCodeGroups]
  );

  // Filter OE code groups
  const filteredOeGroups = useMemo(() => {
    let groups = allOeCodeGroups;
    if (topicSearch) {
      const term = topicSearch.toLowerCase();
      groups = groups.filter(
        (g) =>
          g.oeCode.toLowerCase().includes(term) ||
          g.topicName.toLowerCase().includes(term) ||
          g.keywords.some((k) => k.toLowerCase().includes(term))
      );
    }
    if (topicDifficultyFilter !== 'all') {
      groups = groups.filter((g) => g.difficulty === topicDifficultyFilter);
    }
    return groups;
  }, [allOeCodeGroups, topicSearch, topicDifficultyFilter]);

  // Count of individually selected topics
  const selectedTopicCount = selectedTopicKeys.size;

  // OE codes that have at least one selected topic
  const selectedOeCodesSet = useMemo(() => {
    const oes = new Set<string>();
    for (const group of allOeCodeGroups) {
      if (group.subItems.some((s) => selectedTopicKeys.has(s.key))) {
        oes.add(group.oeCode);
      }
    }
    return oes;
  }, [allOeCodeGroups, selectedTopicKeys]);

  // Groups that have at least one selected topic (for summary chips)
  const selectedOeGroupsList = useMemo(
    () => allOeCodeGroups.filter((g) => selectedOeCodesSet.has(g.oeCode)),
    [allOeCodeGroups, selectedOeCodesSet]
  );

  // ── Handlers ──

  const togglePdf = (id: string) => {
    setSelectedPdfIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Remove OE codes that only belonged to this PDF
        // (keep OE codes that exist in other selected PDFs)
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllPdfs = () => {
    // NQG-RQ-26 v2: skip analyses without identified topics
    setSelectedPdfIds(new Set(completedAnalyses.filter((a) => !hasNoTopics(a)).map((a) => a._id)));
  };

  const clearPdfs = () => {
    setSelectedPdfIds(new Set());
    setSelectedTopicKeys(new Set());
  };

  // Toggle a single sub-item topic
  const toggleTopicKey = (key: string) => {
    setSelectedTopicKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle all sub-items of an OE code group (select-all / deselect-all)
  const toggleOeCodeGroup = (group: OeCodeGroup) => {
    const allKeys = group.subItems.map((s) => s.key);
    const allSelected = allKeys.every((k) => selectedTopicKeys.has(k));
    setSelectedTopicKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allKeys.forEach((k) => next.delete(k));
      } else {
        allKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  // Remove all topics of an OE code (for the X button on summary chips)
  const removeOeCodeTopics = (group: OeCodeGroup) => {
    setSelectedTopicKeys((prev) => {
      const next = new Set(prev);
      group.subItems.forEach((s) => next.delete(s.key));
      return next;
    });
  };

  const selectAllTopics = () => {
    const allKeys = filteredOeGroups.flatMap((g) => g.subItems.map((s) => s.key));
    setSelectedTopicKeys((prev) => {
      const next = new Set(prev);
      allKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const clearTopics = () => {
    setSelectedTopicKeys(new Set());
  };

  const handleGenerate = async () => {
    // Parse selectedTopicKeys into selectedTopics array
    // Key format: "oeCode::pdfId::idx"
    const selectedTopicsArray = Array.from(selectedTopicKeys).map((key) => {
      const parts = key.split('::');
      return {
        pdfId: parts[1],
        oeCode: parts[0],
        topicIndex: parseInt(parts[2], 10),
      };
    });

    if (selectedTopicsArray.length === 0) {
      showToast('error', 'No Topics', 'Please select at least one topic.');
      return;
    }

    // Validate required fields
    if (!questionStyle) {
      showToast('error', 'Missing Field', 'Please select a Question Style.');
      return;
    }
    // Additional Instructions is only required for 'conceptual' style
    if (questionStyle === 'conceptual' && !additionalInstructions.trim()) {
      showToast('error', 'Missing Field', 'Please provide Additional Instructions for Conceptual questions.');
      return;
    }

    // Validate difficulty distribution
    const totalDistribution = difficultyDistribution.easy + difficultyDistribution.medium + difficultyDistribution.hard;
    if (totalDistribution === 0) {
      showToast('error', 'Invalid Distribution', 'Please specify at least one difficulty level count.');
      return;
    }
    if (totalDistribution > count) {
      showToast('error', 'Invalid Distribution', `Total difficulty distribution (${totalDistribution}) cannot exceed the number of questions (${count}).`);
      return;
    }

    setIsGenerating(true);

    try {
      const isCustom = generateTab === 'custom';
      const apiType = QUESTION_TYPE_API_MAP[questionType] || 'mcq';
      const mcqFormatParam = questionType === 'multiple_choice' ? mcqFormat : undefined;

      let result: any;

      if (isCustom) {
        // Use dedicated custom-prompt API (same settings as Generation Settings + prompt)
        result = await pdfAnalysisService.generateQuestionsCustomPrompt({
          prompt: freeformPrompt,
          selectedTopics: selectedTopicsArray,
          questionType: apiType,
          difficultyDistribution,
          count,
          mcqFormat: mcqFormatParam,
          additionalInstructions: additionalInstructions || undefined,
          questionStyle,
        });
      } else {
        // Async generation (client timeout fix): enqueue + poll for progress —
        // large batches can run for as long as they need, no HTTP timeout
        const { jobId } = await pdfAnalysisService.generateQuestionsAsync({
          questionType: apiType,
          difficultyDistribution,
          count,
          mcqFormat: mcqFormatParam,
          additionalInstructions: additionalInstructions || undefined,
          questionStyle,
          selectedTopics: selectedTopicsArray,
        });

        setGenerationProgress({ completed: 0, total: count });
        const job = await new Promise<any>((resolve, reject) => {
          const poll = async () => {
            try {
              const status = await pdfAnalysisService.getGenerationJob(jobId);
              if (status.progress) setGenerationProgress(status.progress);
              if (status.status === 'completed') return resolve(status);
              if (status.status === 'failed') return reject(new Error(status.error || 'Generation failed'));
              setTimeout(poll, 4000);
            } catch (err) {
              // Transient polling error (network blip) — keep polling
              setTimeout(poll, 6000);
            }
          };
          poll();
        });

        result = { success: true, data: job.result };
      }

      if (result.success && result.data) {
        const { summary } = result.data;
        setGeneratedQuestions((prev) => [...prev, ...result.data.questions]);
        showToast(
          'success',
          'Questions Generated',
          `${summary.successful} of ${summary.total} questions generated successfully in ${(summary.totalTimeMs / 1000).toFixed(1)}s.${summary.failed > 0 ? ` ${summary.failed} failed.` : ''}`
        );
        // Redirect to question bank after successful generation
        setTimeout(() => {
          navigate('/pdf-analysis/question-bank');
        }, 2000);
      } else {
        showToast('error', 'Generation Failed', 'No questions were generated. Please try again.');
      }
    } catch (error: any) {
      console.error('[PdfQuestionGenerator] Generation error:', error);
      const message = error.response?.data?.error || error.message || 'An unexpected error occurred';
      showToast('error', 'Generation Error', message);
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  const activeCount = count;

  // ── Render ──

  if (isLoadingList) return <DashboardSkeleton />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-orange-50/20 -m-6 p-6">
      <div className="max-w-[1800px] mx-auto space-y-6 animate-in">
        {/* Header */}
        <PageHeader
          title="Question Generator"
          description="Select PDFs and topics to generate questions across multiple analyses"
          breadcrumbs={[
            { label: 'PDF Analysis', path: '/pdf-analysis' },
            { label: 'Question Generator' },
          ]}
        />

        {/* Stats Cards & Steps - Single Row */}
        <div className="flex items-center gap-6 flex-wrap">
            {/* PDFs Card */}
            <div className="flex-shrink-0 bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedPdfListItems.length}
                  </p>
                  <p className="text-xs text-gray-500 font-medium">PDFs</p>
                </div>
              </div>
            </div>

            {/* Topics Card */}
            <div className="flex-shrink-0 bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-50">
                  <Target className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedTopicCount}
                  </p>
                  <p className="text-xs text-gray-500 font-medium">Topics</p>
                </div>
              </div>
            </div>

            {/* Questions Card */}
            <div className="flex-shrink-0 bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedTopicCount > 0 ? activeCount : 0}
                  </p>
                  <p className="text-xs text-gray-500 font-medium">Questions</p>
                </div>
              </div>
            </div>

            {/* Generated Card - Only show if questions generated */}
            {generatedQuestions.length > 0 && (
              <div className="flex-shrink-0 bg-gradient-to-br from-white via-white to-green-50/20 rounded-2xl border-2 border-green-200/50 p-5 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:scale-105 group animate-in">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-green-400 to-green-500 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-4xl font-black bg-gradient-to-br from-green-600 via-green-500 to-green-600 bg-clip-text text-transparent drop-shadow-sm">
                      {generatedQuestions.length}
                    </p>
                    <p className="text-xs text-gray-700 font-bold mt-1 uppercase tracking-wide">Generated</p>
                  </div>
                </div>
              </div>
            )}

            {/* Spacer */}
            <div className="flex-grow" />

            {/* Step Indicators - Inline with Enhanced Design */}
            <div className="flex-shrink-0 flex items-center gap-4 bg-gradient-to-r from-blue-50/40 to-orange-50/40 rounded-2xl p-4 border border-gray-200/50 shadow-inner">
              {[
                { step: 1, label: 'SELECT PDFS', reachable: true },
                { step: 2, label: 'CHOOSE TOPICS', reachable: selectedPdfListItems.length > 0 },
                { step: 3, label: 'GENERATE', reachable: selectedTopicCount > 0 }
              ].map(({ step, label, reachable }, idx) => {
                const active = currentStep === step;
                return (
                <div key={step} className="flex items-center gap-4">
                  <div
                    className={`text-center group/step ${reachable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    onClick={() => { if (reachable) setCurrentStep(step as 1 | 2 | 3); }}
                    title={reachable ? `Go to step ${step}` : 'Complete the previous step first'}
                  >
                    <div
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black transition-all duration-300 shadow-xl border-2 ${
                        active
                          ? 'bg-gradient-to-br from-[#1e73be] via-[#4A90C8] to-[#1e73be] text-white border-white group-hover/step:scale-110 group-hover/step:rotate-6 group-hover/step:shadow-2xl ring-4 ring-[#1e73be]/30'
                          : reachable
                            ? 'bg-gradient-to-br from-blue-100 to-blue-200 text-[#1e73be] border-blue-200'
                            : 'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-400 border-gray-300'
                      }`}
                    >
                      {step}
                    </div>
                    <p className={`text-[10px] mt-2.5 font-black uppercase tracking-widest transition-all duration-300 ${
                      active ? 'text-[#1e73be] group-hover/step:scale-105 group-hover/step:text-[#4A90C8]' : reachable ? 'text-[#1e73be]/60' : 'text-gray-400'
                    }`}>
                      {label}
                    </p>
                  </div>
                  {idx < 2 && (
                    <div className={`w-20 h-1.5 mb-8 rounded-full transition-all duration-500 relative overflow-hidden ${
                      currentStep > step ? 'bg-[#1e73be] shadow-lg' : 'bg-gray-300'
                    }`}>
                      {currentStep > step && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse" />
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
        </div>

        {/* ═══ STEP 1: Select PDFs ═══ */}
        {currentStep === 1 && (
        <section className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl border-2 border-white/50 transition-all duration-300 overflow-hidden">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(30,115,190,0.3)_1px,transparent_1px)] bg-[length:24px_24px]" />
          </div>
          <div className="relative flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1e73be] to-[#4A90C8] text-white flex items-center justify-center text-lg font-black shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                1
              </div>
              <div>
                <h2 className="text-2xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-600 bg-clip-text text-transparent">
                  Select PDFs
                </h2>
                <p className="text-sm text-gray-600 mt-1 font-medium">
                  Choose PDF analyses to generate questions from
                </p>
              </div>
              {selectedPdfListItems.length > 0 && (
                <div className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-extrabold shadow-lg animate-in hover:scale-105 transition-transform duration-300">
                  {selectedPdfListItems.length} selected
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={selectAllPdfs}
                className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[#1e73be] to-[#4A90C8] text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105"
              >
                Select All
              </button>
              <button
                onClick={clearPdfs}
                className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Search Input */}
          {completedAnalyses.length > 0 && (
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search PDFs by filename..."
                  value={pdfSearch}
                  onChange={(e) => setPdfSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#1e73be] focus:ring-2 focus:ring-[#1e73be]/20 outline-none transition-all bg-white/80 backdrop-blur-sm"
                />
                {pdfSearch && (
                  <button
                    onClick={() => setPdfSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              {/* NQG-RQ-31: portfolio-first drill-down */}
              <div className="mt-3 grid grid-cols-1 lg:grid-cols-4 gap-2 items-start">
                <select
                  value={pdfPortfolioFilter}
                  onChange={(e) => setPdfPortfolioFilter(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm font-medium border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be]/20 focus:border-[#1e73be] bg-white/80"
                >
                  <option value="all">All portfolios</option>
                  {generatorPortfolios.map((p) => (
                    <option key={p._id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <div className="lg:col-span-3">
                  <TaxonomyCascade
                    portfolioId={selectedPortfolioId}
                    value={pdfTaxonomyNode}
                    onChange={(nodeId) => setPdfTaxonomyNode(nodeId)}
                    compact
                  />
                </div>
              </div>
              {(pdfSearch || pdfPortfolioFilter !== 'all' || pdfTaxonomyNode) && (
                <p className="text-xs text-gray-500 mt-2">
                  Found {filteredPdfs.length} of {completedAnalyses.length} PDFs
                </p>
              )}
            </div>
          )}

          {completedAnalyses.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState
                icon={FileText}
                title="No completed analyses"
                description="Upload and analyze PDFs first, then come back to generate questions across them."
                action={{ label: 'Go to PDF Analysis', onClick: () => navigate('/pdf-analysis') }}
              />
            </div>
          ) : filteredPdfs.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No PDFs found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or portfolio/taxonomy filters</p>
              <button
                onClick={() => { setPdfSearch(''); setPdfPortfolioFilter('all'); setPdfTaxonomyNode(null); }}
                className="mt-4 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1e73be] text-white hover:shadow-lg transition-all"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                {pagedPdfs.map((analysis) => (
                  <PdfCard
                    key={analysis._id}
                    analysis={analysis}
                    selected={selectedPdfIds.has(analysis._id)}
                    disabled={hasNoTopics(analysis)}
                    onToggle={() => togglePdf(analysis._id)}
                  />
                ))}
              </div>

              {/* NQG-RQ-28: pagination controls for the PDF list */}
              {pdfTotalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200/70">
                  <p className="text-xs text-gray-500 font-semibold">
                    Showing {(pdfPage - 1) * PDF_PAGE_SIZE + 1}–
                    {Math.min(pdfPage * PDF_PAGE_SIZE, filteredPdfs.length)} of {filteredPdfs.length} PDFs
                  </p>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setPdfPage(1)} disabled={pdfPage === 1}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">« First</button>
                    <button onClick={() => setPdfPage((p) => Math.max(1, p - 1))} disabled={pdfPage === 1}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">‹ Prev</button>
                    <span className="px-3 py-1.5 text-xs font-bold text-gray-700">Page {pdfPage} of {pdfTotalPages}</span>
                    <button onClick={() => setPdfPage((p) => Math.min(pdfTotalPages, p + 1))} disabled={pdfPage === pdfTotalPages}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">Next ›</button>
                    <button onClick={() => setPdfPage(pdfTotalPages)} disabled={pdfPage === pdfTotalPages}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">Last »</button>
                  </div>
                </div>
              )}

              {/* NQG-RQ-28: Step 1 → Step 2 navigation */}
              <div className="flex justify-end mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={selectedPdfListItems.length === 0}
                  className="px-8 py-3.5 rounded-2xl text-sm font-extrabold bg-gradient-to-r from-[#1e73be] to-[#4A90C8] text-white shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-2"
                  title={selectedPdfListItems.length === 0 ? 'Select at least one PDF to continue' : 'Continue to topic selection'}
                >
                  Next: Choose Topics →
                </button>
              </div>
            </>
          )}
        </section>
        )}

        {/* ═══ STEP 2: Select Topics (grouped by OE Code) ═══ */}
        {currentStep === 2 && selectedPdfListItems.length > 0 && (
          <section className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl border-2 border-white/50 transition-all duration-300 overflow-hidden animate-in">
            {/* Decorative background */}
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(234,117,0,0.3)_1px,transparent_1px)] bg-[length:24px_24px]" />
            </div>
            <div className="relative flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#ea7500] to-[#f59e42] text-white flex items-center justify-center text-lg font-black shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  2
                </div>
                <div>
                  <h2 className="text-2xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-600 bg-clip-text text-transparent">
                    Select Topics
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 font-medium">
                    {allOeCodeGroups.length} OE codes · {totalTopicCount} topics available
                  </p>
                </div>
                {selectedTopicCount > 0 && (
                  <div className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-extrabold shadow-lg animate-in hover:scale-105 transition-transform duration-300">
                    {selectedTopicCount} topic{selectedTopicCount !== 1 ? 's' : ''} selected
                  </div>
                )}
                {isLoadingDetails && (
                  <Loader2 className="w-6 h-6 text-[#ea7500] animate-spin" />
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAllTopics}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[#ea7500] to-[#f59e42] text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                >
                  Select All
                </button>
                <button
                  onClick={clearTopics}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Enhanced Filter bar */}
            <div className="relative bg-gradient-to-r from-white via-gray-50 to-white rounded-2xl border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 p-5 mb-6">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by OE code, topic name, or keyword..."
                    value={topicSearch}
                    onChange={(e) => setTopicSearch(e.target.value)}
                    className="input-field w-full pl-12 pr-10 py-3 text-sm rounded-xl border-gray-300 focus:border-[#ea7500] focus:ring-[#ea7500]"
                  />
                  {topicSearch && (
                    <button
                      onClick={() => setTopicSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <select
                  value={topicDifficultyFilter}
                  onChange={(e) => setTopicDifficultyFilter(e.target.value)}
                  className="input-field text-sm py-3 px-4 rounded-xl border-gray-300 focus:border-[#ea7500] focus:ring-[#ea7500] min-w-[180px]"
                >
                  <option value="all">All Difficulties</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>

            {/* Enhanced Selected topics summary */}
            {selectedTopicCount > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5 mb-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-bold text-blue-900">
                    {selectedTopicCount} topic{selectedTopicCount !== 1 ? 's' : ''} selected from {selectedOeGroupsList.length} OE code{selectedOeGroupsList.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedOeGroupsList.map((g) => {
                    const selectedCount = g.subItems.filter((s) => selectedTopicKeys.has(s.key)).length;
                    return (
                      <span
                        key={g.oeCode}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border-2 border-blue-300 text-xs font-mono font-bold text-[#1e73be] shadow-sm hover:shadow-md transition-all"
                      >
                        <span>{g.oeCode}</span>
                        {g.itemCount > 1 && (
                          <span className="px-2 py-0.5 rounded-lg bg-blue-50 text-[10px] font-normal text-blue-600">
                            {selectedCount}/{g.itemCount}
                          </span>
                        )}
                        <button
                          onClick={() => removeOeCodeTopics(g)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Enhanced OE Code groups cards */}
            {isLoadingDetails ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#ea7500] animate-spin mb-3" />
                <span className="text-sm font-medium text-gray-600">Loading topics...</span>
              </div>
            ) : filteredOeGroups.length === 0 ? (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 shadow-sm">
                <EmptyState
                  icon={Filter}
                  title="No topics match"
                  description="Try adjusting your search or difficulty filter."
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                {filteredOeGroups.map((group) => (
                  <OeCodeCard
                    key={group.oeCode}
                    group={group}
                    selectedTopicKeys={selectedTopicKeys}
                    onToggleGroup={() => toggleOeCodeGroup(group)}
                    onToggleTopic={toggleTopicKey}
                  />
                ))}
              </div>
            )}

            {/* NQG-RQ-28: Step 2 navigation */}
            <div className="flex justify-between mt-8 pt-4 border-t border-gray-200/70">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3.5 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-300 flex items-center gap-2"
              >
                ← Back to PDFs
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                disabled={selectedTopicCount === 0}
                className="px-8 py-3.5 rounded-2xl text-sm font-extrabold bg-gradient-to-r from-[#ea7500] to-[#f59e42] text-white shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-2"
                title={selectedTopicCount === 0 ? 'Select at least one topic to continue' : 'Continue to configuration'}
              >
                Next: Configure & Generate →
              </button>
            </div>
          </section>
        )}

        {/* ═══ STEP 3: Configure & Generate ═══ */}
        {currentStep === 3 && selectedTopicCount > 0 && (
          <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/40">
            {/* NQG-RQ-28: Step 3 back navigation */}
            <div className="flex justify-start mb-4">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-2.5 rounded-xl text-sm font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-300 flex items-center gap-2"
              >
                ← Back to Topics
              </button>
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center text-base font-bold shadow-lg">
                3
              </div>
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Configure & Generate
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Set generation parameters and create questions
                </p>
              </div>
            </div>

            {/* Enhanced Tabs */}
            <div className="flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 p-1.5 rounded-2xl mb-6 w-fit shadow-inner">
              <button
                onClick={() => setGenerateTab('settings')}
                className={`flex items-center gap-3 px-6 py-3.5 rounded-2xl text-sm font-extrabold transition-all duration-300 ${
                  generateTab === 'settings'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-xl ring-4 ring-emerald-500/30'
                    : 'bg-white text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-md'
                }`}
              >
                <Sparkles className="w-5 h-5" />
                Quick Generate
              </button>
              <button
                onClick={() => setGenerateTab('custom')}
                className={`flex items-center gap-3 px-6 py-3.5 rounded-2xl text-sm font-extrabold transition-all duration-300 ${
                  generateTab === 'custom'
                    ? 'bg-gradient-to-r from-[#1e73be] to-[#4A90C8] text-white shadow-xl ring-4 ring-[#1e73be]/30'
                    : 'bg-white text-gray-600 hover:text-[#1e73be] hover:bg-blue-50 border-2 border-gray-200 hover:border-[#1e73be] hover:shadow-md'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                Custom Prompt
              </button>
            </div>

            {/* Tab 1: Quick Generate */}
            {generateTab === 'settings' && (
            <>
              <div className="bg-gradient-to-br from-white via-emerald-50/20 to-green-50/30 rounded-2xl border-2 border-emerald-200/50 shadow-xl p-6">
                <h3 className="font-extrabold text-gray-900 mb-6 flex items-center gap-3 text-xl">
                  <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  Quick Generate Settings
                </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Question Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Question Type
                    {mcqFormat === 'non-anbima' && (
                      <span className="ml-2 text-xs font-normal text-amber-600">(Locked to MCQ for Non-ANBIMA)</span>
                    )}
                  </label>
                  <select
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                    disabled={mcqFormat === 'non-anbima'}
                    className={`input-field w-full ${mcqFormat === 'non-anbima' ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Count */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Number of Questions
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={count}
                    onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                    className="input-field w-full"
                  />
                </div>
                   {/* MCQ Format */}
                {questionType === 'multiple_choice' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">MCQ Format</label>
                    <select
                      value={mcqFormat}
                      onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'non-anbima')}
                      className="input-field w-full"
                    >
                      <option value="anbima">ANBIMA</option>
                      <option value="non-anbima">Non-ANBIMA</option>
                    </select>
                  </div>
                )}

                {/* Difficulty Distribution */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Difficulty Distribution
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (Total: {difficultyDistribution.easy + difficultyDistribution.medium + difficultyDistribution.hard} / {count})
                    </span>
                  </label>

                  <div className="space-y-4">
                    {/* Easy Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-green-700">
                          Easy
                        </label>
                        <span className="text-sm font-semibold text-green-700">
                          {difficultyPercentages.easy}% ({difficultyDistribution.easy} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.easy}
                        onChange={(e) => {
                          const newEasy = parseInt(e.target.value);
                          const remaining = 100 - newEasy;
                          const currentMedium = difficultyPercentages.medium;
                          const currentHard = difficultyPercentages.hard;
                          const otherTotal = currentMedium + currentHard;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const mediumRatio = currentMedium / otherTotal;
                            const newMedium = Math.round(remaining * mediumRatio);
                            const newHard = remaining - newMedium;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: newEasy, medium: remaining, hard: 0 });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-green"
                        style={{
                          background: `linear-gradient(to right, #22c55e 0%, #22c55e ${difficultyPercentages.easy}%, #e5e7eb ${difficultyPercentages.easy}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>

                    {/* Medium Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-yellow-700">
                          Medium
                        </label>
                        <span className="text-sm font-semibold text-yellow-700">
                          {difficultyPercentages.medium}% ({difficultyDistribution.medium} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.medium}
                        onChange={(e) => {
                          const newMedium = parseInt(e.target.value);
                          const remaining = 100 - newMedium;
                          const currentEasy = difficultyPercentages.easy;
                          const currentHard = difficultyPercentages.hard;
                          const otherTotal = currentEasy + currentHard;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const easyRatio = currentEasy / otherTotal;
                            const newEasy = Math.round(remaining * easyRatio);
                            const newHard = remaining - newEasy;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: remaining, medium: newMedium, hard: 0 });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-yellow"
                        style={{
                          background: `linear-gradient(to right, #eab308 0%, #eab308 ${difficultyPercentages.medium}%, #e5e7eb ${difficultyPercentages.medium}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>

                    {/* Hard Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-red-700">
                          Hard
                        </label>
                        <span className="text-sm font-semibold text-red-700">
                          {difficultyPercentages.hard}% ({difficultyDistribution.hard} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.hard}
                        onChange={(e) => {
                          const newHard = parseInt(e.target.value);
                          const remaining = 100 - newHard;
                          const currentEasy = difficultyPercentages.easy;
                          const currentMedium = difficultyPercentages.medium;
                          const otherTotal = currentEasy + currentMedium;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const easyRatio = currentEasy / otherTotal;
                            const newEasy = Math.round(remaining * easyRatio);
                            const newMedium = remaining - newEasy;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: remaining, medium: 0, hard: newHard });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-red"
                        style={{
                          background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${difficultyPercentages.hard}%, #e5e7eb ${difficultyPercentages.hard}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Question Style */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Question Style <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={questionStyle}
                    onChange={(e) => setQuestionStyle(e.target.value as 'conceptual' | 'scenario' | 'mixed')}
                    className="input-field w-full"
                    required
                  >
                    <option value="conceptual">Conceptual</option>
                    <option value="scenario">Scenario</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>

                {/* Additional Instructions */}
                <div className="md:col-span-2 lg:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Additional Instructions {questionStyle === 'conceptual' && <span className="text-red-500">*</span>}
                    {questionStyle === 'conceptual' && <span className="ml-1 text-xs font-normal text-gray-500">(Required for Conceptual)</span>}
                  </label>
                  <input
                    type="text"
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    className="input-field w-full"
                    placeholder="e.g., perguntas curtas sem personagens"
                    required={questionStyle === 'conceptual'}
                  />
                </div>

              </div>

            </div>

            {/* Validation Rules Panel */}
            {isLoadingTypeConfig ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-[#1e73be] animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Loading rules...</span>
              </div>
            ) : typeConfig ? (
              <TypeConfigPanel config={typeConfig} mcqFormat={mcqFormat} />
            ) : null}
          </>
          )}

          {/* Tab 2: Custom Prompt */}
          {generateTab === 'custom' && (
          <>
            <div className="bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30 rounded-2xl border-2 border-blue-200/50 shadow-xl p-6">
              <h3 className="font-extrabold text-gray-900 mb-6 flex items-center gap-3 text-xl">
                <div className="p-2.5 bg-gradient-to-br from-[#1e73be] to-[#4A90C8] rounded-2xl shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                Quick Generate Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Question Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Question Type
                    {mcqFormat === 'non-anbima' && (
                      <span className="ml-2 text-xs font-normal text-amber-600">(Locked to MCQ for Non-ANBIMA)</span>
                    )}
                  </label>
                  <select
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                    disabled={mcqFormat === 'non-anbima'}
                    className={`input-field w-full ${mcqFormat === 'non-anbima' ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Number of Questions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Number of Questions
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={count}
                    onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                    className="input-field w-full"
                  />
                </div>

                {/* MCQ Format */}
                {questionType === 'multiple_choice' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">MCQ Format</label>
                    <select
                      value={mcqFormat}
                      onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'non-anbima')}
                      className="input-field w-full"
                    >
                      <option value="anbima">ANBIMA</option>
                      <option value="non-anbima">Non-ANBIMA</option>
                    </select>
                  </div>
                )}

                {/* Difficulty Distribution */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Difficulty Distribution
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (Total: {difficultyDistribution.easy + difficultyDistribution.medium + difficultyDistribution.hard} / {count})
                    </span>
                  </label>

                  <div className="space-y-4">
                    {/* Easy Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-green-700">
                          Easy
                        </label>
                        <span className="text-sm font-semibold text-green-700">
                          {difficultyPercentages.easy}% ({difficultyDistribution.easy} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.easy}
                        onChange={(e) => {
                          const newEasy = parseInt(e.target.value);
                          const remaining = 100 - newEasy;
                          const currentMedium = difficultyPercentages.medium;
                          const currentHard = difficultyPercentages.hard;
                          const otherTotal = currentMedium + currentHard;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const mediumRatio = currentMedium / otherTotal;
                            const newMedium = Math.round(remaining * mediumRatio);
                            const newHard = remaining - newMedium;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: newEasy, medium: remaining, hard: 0 });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-green"
                        style={{
                          background: `linear-gradient(to right, #22c55e 0%, #22c55e ${difficultyPercentages.easy}%, #e5e7eb ${difficultyPercentages.easy}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>

                    {/* Medium Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-yellow-700">
                          Medium
                        </label>
                        <span className="text-sm font-semibold text-yellow-700">
                          {difficultyPercentages.medium}% ({difficultyDistribution.medium} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.medium}
                        onChange={(e) => {
                          const newMedium = parseInt(e.target.value);
                          const remaining = 100 - newMedium;
                          const currentEasy = difficultyPercentages.easy;
                          const currentHard = difficultyPercentages.hard;
                          const otherTotal = currentEasy + currentHard;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const easyRatio = currentEasy / otherTotal;
                            const newEasy = Math.round(remaining * easyRatio);
                            const newHard = remaining - newEasy;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: remaining, medium: newMedium, hard: 0 });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-yellow"
                        style={{
                          background: `linear-gradient(to right, #eab308 0%, #eab308 ${difficultyPercentages.medium}%, #e5e7eb ${difficultyPercentages.medium}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>

                    {/* Hard Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-red-700">
                          Hard
                        </label>
                        <span className="text-sm font-semibold text-red-700">
                          {difficultyPercentages.hard}% ({difficultyDistribution.hard} questions)
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={difficultyPercentages.hard}
                        onChange={(e) => {
                          const newHard = parseInt(e.target.value);
                          const remaining = 100 - newHard;
                          const currentEasy = difficultyPercentages.easy;
                          const currentMedium = difficultyPercentages.medium;
                          const otherTotal = currentEasy + currentMedium;

                          if (otherTotal > 0) {
                            // Auto-adjust other sliders proportionally to maintain 100%
                            const easyRatio = currentEasy / otherTotal;
                            const newEasy = Math.round(remaining * easyRatio);
                            const newMedium = remaining - newEasy;
                            setDifficultyPercentages({ easy: newEasy, medium: newMedium, hard: newHard });
                          } else {
                            setDifficultyPercentages({ easy: remaining, medium: 0, hard: newHard });
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-red"
                        style={{
                          background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${difficultyPercentages.hard}%, #e5e7eb ${difficultyPercentages.hard}%, #e5e7eb 100%)`
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Question Style & Additional Instructions Row */}
                <div className="grid grid-cols-2 gap-4 md:col-span-2 lg:col-span-3">
                  {/* Question Style */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Question Style <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={questionStyle}
                      onChange={(e) => setQuestionStyle(e.target.value as 'conceptual' | 'scenario' | 'mixed')}
                      className="input-field w-full"
                      required
                    >
                      <option value="conceptual">Conceptual</option>
                      <option value="scenario">Scenario</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>

                  {/* Additional Instructions */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Additional Instructions {questionStyle === 'conceptual' && (
                        <>
                          <span className="text-red-500">*</span>
                          <span className="ml-1 text-xs font-normal text-gray-500">(Required for Conceptual)</span>
                        </>
                      )}
                    </label>
                    <input
                      type="text"
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      className="input-field w-full"
                      placeholder="e.g., perguntas curtas sem personagens"
                      required={questionStyle === 'conceptual'}
                    />
                  </div>
                </div>
              </div>

              {/* Custom Prompt */}
              <div className="mt-6 pt-6 border-t-2 border-blue-200">
                <label className="block text-lg font-extrabold text-gray-900 mb-2 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  Custom Prompt
                </label>
                <p className="text-sm text-gray-600 mb-4 font-medium">
                  Write your own prompt. The selected topics and PDF content will be provided as context.
                </p>

                {/* Example prompt chips */}
                <div className="mb-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Examples</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Calculation with formula', prompt: 'Create a calculation question where the student must compute the result step-by-step using the topic formulas. Include all numeric data in the stem.' },
                      { label: 'FCFF vs FCFE', prompt: 'Generate a conceptual question about the difference between FCFF and FCFE, focusing on when to use WACC vs CAPM as discount rate.' },
                      { label: 'Common mistakes', prompt: 'Create questions that test common student mistakes — forgetting to subtract variables, using percentages instead of decimals, confusing related concepts.' },
                      { label: 'Advantages / Disadvantages', prompt: 'Generate a question about the advantages and disadvantages of the method described in this topic. Use the vantagens and desvantagens from the topic data.' },
                      { label: 'Exam alerts focus', prompt: 'Focus on the examAlerts from this topic. Create a tricky question that tests the most commonly confused concept mentioned in the alerts.' },
                      { label: 'Simple (pt-BR)', prompt: 'Gere uma questão conceitual sobre este tópico, nível médio, com 4 alternativas plausíveis.' },
                    ].map((ex) => (
                      <button
                        key={ex.label}
                        type="button"
                        onClick={() => setFreeformPrompt(ex.prompt)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-[#1e73be]/10 hover:text-[#1e73be] border border-transparent hover:border-[#1e73be]/20 transition-all"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  value={freeformPrompt}
                  onChange={(e) => setFreeformPrompt(e.target.value)}
                  placeholder={"e.g. Create a calculation question where the student must compute FCFF using net income, depreciation, and capital expenditure. Include all numeric data in the stem..."}
                  rows={5}
                  className="input-field w-full resize-none"
                />
              </div>
            </div>

            {/* Validation Rules Panel — same as settings tab */}
            {isLoadingTypeConfig ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-[#1e73be] animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Loading rules...</span>
              </div>
            ) : typeConfig ? (
              <TypeConfigPanel config={typeConfig} mcqFormat={mcqFormat} />
            ) : null}
          </>
          )}

            {/* Enhanced Generate button section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-emerald-50 via-blue-50 to-purple-50 rounded-2xl border border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-2xl shadow-md">
                    <Sparkles className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Ready to Generate</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      <span className="font-semibold text-emerald-600">{selectedPdfListItems.length}</span> PDF{selectedPdfListItems.length !== 1 ? 's' : ''}
                      {' · '}
                      <span className="font-semibold text-blue-600">{selectedTopicCount}</span> topic{selectedTopicCount !== 1 ? 's' : ''}
                      {' · '}
                      <span className="font-semibold text-purple-600">{selectedOeGroupsList.length}</span> OE code{selectedOeGroupsList.length !== 1 ? 's' : ''}
                      {' · '}
                      <span className="font-semibold text-gray-900">{activeCount}</span> question{activeCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || (generateTab === 'custom' && !freeformPrompt.trim())}
                  // translate="no": this label re-renders every few seconds during
                  // generation (live progress) — browser auto-translate rewriting
                  // it mid-update is what crashed the page (client demo report)
                  translate="no"
                  className="px-8 py-4 rounded-2xl font-bold text-base flex items-center gap-3 bg-gradient-to-r from-emerald-500 via-emerald-600 to-green-600 text-white shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 hover:-translate-y-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:hover:shadow-2xl"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {generationProgress
                        ? `Generating... ${generationProgress.completed}/${generationProgress.total} done`
                        : `Generating ${activeCount} questions...`}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate {activeCount} Questions
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ═══ ENHANCED RESULTS PANEL - HIDDEN (redirects to question bank instead) ═══ */}
        {false && generatedQuestions.length > 0 && (
          <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/40">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Generated Questions
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {generatedQuestions.length} question{generatedQuestions.length !== 1 ? 's' : ''} successfully generated
                  </p>
                </div>
              </div>
              <button
                onClick={() => setGeneratedQuestions([])}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            </div>

            <div className="space-y-4">
              {generatedQuestions.map((q, idx) => (
                <GeneratedQuestionCard key={q._id || idx} question={q} index={idx} />
              ))}
            </div>
          </section>
        )}

        {/* Enhanced Empty hint when no PDFs selected */}
        {completedAnalyses.length > 0 && selectedPdfListItems.length === 0 && (
          <div className="bg-gradient-to-br from-white/80 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-gray-200 shadow-xl">
            <EmptyState
              icon={Layers}
              title="Select PDFs to begin"
              description="Choose one or more completed PDF analyses above to start selecting topics for question generation."
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Generated Question Card ──────────────────────────────────────────────────

function GeneratedQuestionCard({ question: q, index }: { question: any; index: number }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const typeLabel =
    q.type === 'multiple_choice' ? 'MCQ' :
    q.type === 'short_answer' ? 'Short Answer' :
    q.type === 'case_study' ? 'Case Study' :
    q.type === 'interactive' ? 'Interactive' : q.type;

  const diffMap: Record<string, 'success' | 'warning' | 'error'> = {
    easy: 'success',
    medium: 'warning',
    hard: 'error',
  };

  const correctAlt = q.alternatives?.find((a: any) => a.isCorrect || a.correct);
  const correctLetter = q.correctAnswer || correctAlt?.letter || '—';

  const handleCopyStem = () => {
    navigator.clipboard.writeText(q.stem || '');
  };

  const handleViewInBank = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/pdf-analysis/question-bank');
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden transition-all hover:shadow-xl">
      {/* Enhanced Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all"
      >
        {/* Enhanced Number */}
        <span className="flex-shrink-0 w-9 h-9 rounded-2xl bg-gradient-to-br from-[#1e73be] to-[#4A90C8] text-white flex items-center justify-center text-sm font-bold shadow-md">
          {index + 1}
        </span>

        {/* Stem preview */}
        <span className="flex-1 text-sm font-semibold text-gray-900 truncate">
          {q.stem?.substring(0, 120)}{q.stem?.length > 120 ? '...' : ''}
        </span>

        {/* Enhanced Badges */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {q.oeCode && q.oeCode !== 'N/A' && (
            <span className="font-mono text-xs font-bold text-[#1e73be] bg-blue-100 px-3 py-1 rounded-xl border-2 border-blue-200 shadow-sm">
              {q.oeCode}
            </span>
          )}
          <Badge variant="info" size="sm">{typeLabel}</Badge>
          <Badge variant={diffMap[q.difficulty] || 'neutral'} size="sm">{q.difficulty}</Badge>
          {correctLetter !== '—' && q.type !== 'interactive' && (
            <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 text-white flex items-center justify-center text-xs font-bold shadow-md">
              {correctLetter}
            </span>
          )}
        </div>

        {/* Enhanced Question Bank Button */}
        <button
          onClick={handleViewInBank}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white border border-indigo-300 hover:from-indigo-600 hover:to-purple-600 hover:shadow-lg transition-all duration-200 flex-shrink-0"
          title="View in Question Bank"
        >
          <Database className="w-4 h-4" />
          Question Bank
        </button>

        {/* Enhanced Expand icon */}
        <div className={`p-1.5 rounded-lg transition-all ${expanded ? 'bg-gray-200' : 'bg-gray-100 hover:bg-gray-200'}`}>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          {/* Full stem */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stem</span>
              <button onClick={handleCopyStem} className="text-gray-400 hover:text-[#1e73be] transition-colors" title="Copy stem">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{q.stem}</p>
          </div>

          {/* MCQ Alternatives */}
          {q.alternatives && q.alternatives.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Alternatives</span>
              <div className="space-y-1.5">
                {q.alternatives.map((alt: any) => {
                  const isCorrect = alt.isCorrect || alt.correct;
                  return (
                    <div
                      key={alt.letter}
                      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl text-sm ${
                        isCorrect
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-gray-50 border border-gray-100'
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCorrect
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {alt.letter}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`${isCorrect ? 'text-emerald-800 font-medium' : 'text-gray-700'}`}>
                          {alt.text}
                        </p>
                        {/* {!isCorrect && alt.explanation && (
                          <p className="text-[11px] text-red-500 mt-1">
                            {alt.explanation}
                          </p>
                        )} */}
                      </div>
                      {isCorrect && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Short answer correct answer */}
          {q.type === 'short_answer' && q.correctAnswer && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Model Answer</span>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-sm text-emerald-800 whitespace-pre-wrap">{q.correctAnswer}</p>
              </div>
            </div>
          )}

          {/* Interactive prompts summary */}
          {q.type === 'interactive' && q.prompts && q.prompts.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                Prompts ({q.prompts.length})
              </span>
              <div className="space-y-2">
                {q.prompts.map((p: any, pi: number) => (
                  <div key={p.id || pi} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-[#1e73be] bg-blue-50 px-1.5 py-0.5 rounded">{p.id}</span>
                      {p.role && <span className="text-[10px] text-gray-400 capitalize">{p.role}</span>}
                    </div>
                    <p className="text-xs text-gray-700 mb-2">{p.text}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {p.alternatives?.map((a: any) => (
                        <span
                          key={a.letter}
                          className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            a.score >= 4
                              ? 'bg-emerald-100 text-emerald-700'
                              : a.score >= 2
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {a.letter}: {a.score}pts
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanation toggle */}
          {q.explanation && (
            <div>
              <button
                onClick={() => setShowExplanation(!showExplanation)}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#1e73be] hover:text-[#1e73be]/80 transition-colors"
              >
                {showExplanation ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showExplanation ? 'Hide' : 'Show'} Explanation
              </button>
              {showExplanation && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">{q.explanation}</p>
                </div>
              )}
            </div>
          )}

          {/* Metadata row */}
          <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-gray-100">
            {q.cognitiveLevel && (
              <span className="text-[10px] text-gray-400">
                <span className="font-medium text-gray-500">Cognitive:</span> {q.cognitiveLevel}
              </span>
            )}
            {q.estimatedTime && (
              <span className="text-[10px] text-gray-400">
                <span className="font-medium text-gray-500">Time:</span> {Math.round(q.estimatedTime / 60)}min
              </span>
            )}
            {q.mcqFormat && (
              <span className="text-[10px] text-gray-400">
                <span className="font-medium text-gray-500">Format:</span> {q.mcqFormat}
              </span>
            )}
            {q.sourceDocuments?.[0]?.filename && (
              <span className="text-[10px] text-gray-400 flex items-center gap-1">
                <FileText className="w-2.5 h-2.5" />
                {q.sourceDocuments[0].filename}
              </span>
            )}
            {q._id && (
              <span className="text-[10px] text-gray-400 font-mono flex items-center gap-1 ml-auto">
                <Hash className="w-2.5 h-2.5" />
                {q._id}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function PdfCard({
  analysis,
  selected,
  disabled = false,
  onToggle,
}: {
  analysis: PdfAnalysisListItem;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const diff = analysis.difficultyDistribution;
  const total = (diff?.easy || 0) + (diff?.medium || 0) + (diff?.hard || 0);

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      title={
        disabled
          ? 'No topics were identified in this analysis, so it cannot be used for question generation.'
          : undefined
      }
      className={`group text-left w-full rounded-2xl p-6 transition-all duration-300 ${
        disabled
          ? 'border-2 border-amber-200 bg-gradient-to-br from-amber-50/60 to-gray-50 opacity-70 cursor-not-allowed'
          : selected
          ? 'border-4 border-[#1e73be] bg-gradient-to-br from-blue-50 to-indigo-50 shadow-2xl ring-4 ring-[#1e73be]/20'
          : 'border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 hover:border-[#1e73be]/50 hover:shadow-lg hover:-translate-y-1'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-2xl transition-all duration-300 ${
          selected
            ? 'bg-gradient-to-br from-[#1e73be] to-[#4A90C8] shadow-lg'
            : 'bg-gradient-to-br from-[#1e73be]/10 to-[#ea7500]/10 group-hover:from-[#1e73be]/20 group-hover:to-[#ea7500]/20'
        }`}>
          <FileText className={`w-6 h-6 transition-colors ${selected ? 'text-white' : 'text-[#1e73be]'}`} />
        </div>
        {disabled ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-bold border border-amber-200">
            <AlertTriangle className="w-3 h-3" />
            No topics
          </span>
        ) : (
          <div className={`p-1.5 rounded-xl transition-all ${
            selected ? 'text-[#1e73be] bg-white shadow-md' : 'text-gray-300 group-hover:text-[#1e73be]/50'
          }`}>
            {selected ? <CheckSquare className="w-6 h-6" /> : <Square className="w-6 h-6" />}
          </div>
        )}
      </div>

      <h3 className={`font-bold text-sm truncate mb-2 transition-colors ${
        selected ? 'text-[#1e73be]' : 'text-gray-900 group-hover:text-[#1e73be]'
      }`}>
        {analysis.filename}
      </h3>

      {/* NQG-RQ-29: portfolio tags for this PDF (client feedback) */}
      <div className="flex flex-wrap gap-1 mb-2">
        {((analysis as any).portfolios || []).length === 0 ? (
          <span className="text-[9px] text-gray-400 italic">FK (default)</span>
        ) : (
          (analysis as any).portfolios.map((pf: string) => (
            <span key={pf} className="inline-flex px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[9px] font-semibold text-blue-700">
              {pf}
            </span>
          ))
        )}
      </div>

      <p className="text-xs text-gray-600 mb-4 font-medium">
        {analysis.totalPages} pages
        {analysis.totalItems > 0 && <> · {analysis.totalItems} topics</>}
        {analysis.extractionMode && <> · <span className="capitalize">{analysis.extractionMode}</span></>}
      </p>

      {/* Enhanced Difficulty bar */}
      {total > 0 && diff && (
        <div>
          <div className="flex gap-1 h-2.5 rounded-full overflow-hidden bg-gray-200 shadow-inner">
            {diff.easy > 0 && (
              <div
                className="bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
                style={{ width: `${(diff.easy / total) * 100}%` }}
              />
            )}
            {diff.medium > 0 && (
              <div
                className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"
                style={{ width: `${(diff.medium / total) * 100}%` }}
              />
            )}
            {diff.hard > 0 && (
              <div
                className="bg-gradient-to-r from-red-400 to-red-500 rounded-full"
                style={{ width: `${(diff.hard / total) * 100}%` }}
              />
            )}
          </div>
          <div className="flex justify-between mt-2 text-[10px] font-bold">
            <span className="text-emerald-600">{diff.easy} Easy</span>
            <span className="text-amber-600">{diff.medium} Med</span>
            <span className="text-red-600">{diff.hard} Hard</span>
          </div>
        </div>
      )}
    </button>
  );
}

function OeCodeCard({
  group,
  selectedTopicKeys,
  onToggleGroup,
  onToggleTopic,
}: {
  group: OeCodeGroup;
  selectedTopicKeys: Set<string>;
  onToggleGroup: () => void;
  onToggleTopic: (key: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMultiple = group.itemCount > 1;

  const diffMap: Record<string, 'success' | 'warning' | 'error'> = {
    easy: 'success',
    medium: 'warning',
    hard: 'error',
  };

  const selectedCount = group.subItems.filter((s) => selectedTopicKeys.has(s.key)).length;
  const allSelected = selectedCount === group.itemCount;
  const someSelected = selectedCount > 0;

  // For single-item groups, directly toggle the single sub-item
  const handleGroupClick = () => {
    if (hasMultiple) {
      onToggleGroup();
    } else {
      onToggleTopic(group.subItems[0].key);
    }
  };

  return (
    <div
      className={`group rounded-2xl p-6 transition-all duration-300 ${
        someSelected
          ? 'border-4 border-[#ea7500] bg-gradient-to-br from-orange-50 to-amber-50 shadow-2xl ring-4 ring-[#ea7500]/20'
          : 'border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 hover:border-[#ea7500]/50 hover:shadow-lg hover:-translate-y-1'
      }`}
    >
      {/* Enhanced Header: OE badge + checkbox */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`p-3 rounded-2xl transition-all duration-300 ${
            someSelected
              ? 'bg-gradient-to-br from-[#ea7500] to-[#f59e42] shadow-lg'
              : 'bg-gradient-to-br from-[#ea7500]/10 to-[#1e73be]/10 group-hover:from-[#ea7500]/20 group-hover:to-[#1e73be]/20'
          }`}>
            <Target className={`w-5 h-5 transition-colors ${someSelected ? 'text-white' : 'text-[#ea7500]'}`} />
          </div>
          <span className={`font-mono text-sm font-bold px-3 py-1.5 rounded-xl shadow-sm ${
            someSelected
              ? 'text-[#ea7500] bg-white border-2 border-[#ea7500]'
              : 'text-[#ea7500] bg-orange-50 border-2 border-orange-200'
          }`}>
            {group.oeCode}
          </span>
        </div>
        <button
          onClick={handleGroupClick}
          className={`p-1.5 rounded-xl transition-all ${
            allSelected ? 'text-[#ea7500] bg-white shadow-md' : someSelected ? 'text-[#ea7500]/50 bg-white/50' : 'text-gray-300 group-hover:text-[#ea7500]/50'
          }`}
        >
          {allSelected || (!hasMultiple && someSelected) ? (
            <CheckSquare className="w-6 h-6" />
          ) : someSelected ? (
            <CheckSquare className="w-6 h-6 opacity-50" />
          ) : (
            <Square className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Topic name */}
      <h3
        className={`font-bold text-sm truncate mb-3 transition-colors cursor-pointer ${
          someSelected ? 'text-[#ea7500]' : 'text-gray-900 group-hover:text-[#ea7500]'
        }`}
        onClick={handleGroupClick}
      >
        {group.topicName}
      </h3>

      {/* Difficulty + topic count */}
      <div className="flex items-center gap-2 mb-4">
        <Badge variant={diffMap[group.difficulty] || 'neutral'} size="sm">
          {group.difficulty}
        </Badge>
        {hasMultiple && (
          <span className={`text-xs px-3 py-1 rounded-xl font-bold shadow-sm ${
            someSelected
              ? 'text-[#ea7500] bg-white border-2 border-[#ea7500]'
              : 'text-gray-600 bg-gray-100 border-2 border-gray-200'
          }`}>
            {selectedCount}/{group.itemCount} topics
          </span>
        )}
      </div>

      {/* Enhanced Keywords */}
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {group.keywords.slice(0, 4).map((kw) => (
          <span
            key={kw}
            className="px-2 py-1 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 text-[10px] text-gray-600 font-semibold shadow-sm"
          >
            {kw}
          </span>
        ))}
        {group.keywords.length > 4 && (
          <span className="text-[10px] font-bold text-gray-500 px-2 py-1 bg-gray-100 rounded-lg">+{group.keywords.length - 4}</span>
        )}
      </div>

      {/* Enhanced PDF sources */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {group.pdfSources.map((src) => (
          <span
            key={src.pdfId}
            className="inline-flex items-center gap-1.5 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded-lg shadow-sm"
          >
            <FileText className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{src.filename}</span>
          </span>
        ))}
      </div>

      {/* Enhanced Sub-items: individual topic checkboxes (for multi-topic groups) */}
      {hasMultiple && (
        <div className="border-t border-gray-200 pt-4">
          {/* Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between px-3 py-2 mb-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-200"
          >
            <span className="text-xs font-bold text-gray-700">
              {isExpanded ? 'Hide' : 'Show'} {group.itemCount} Topics
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            )}
          </button>

          {/* Collapsible Sub-items */}
          {isExpanded && (
            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              {group.subItems.map((sub, idx) => {
                const isSelected = selectedTopicKeys.has(sub.key);
                return (
                  <button
                    key={sub.key}
                    onClick={() => onToggleTopic(sub.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs text-left transition-all ${
                      isSelected
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-[#ea7500] shadow-md'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className={`flex-shrink-0 transition-colors ${isSelected ? 'text-[#ea7500]' : 'text-gray-300'}`}>
                      {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </div>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                      isSelected ? 'bg-[#ea7500] text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className={`flex-1 font-semibold truncate ${isSelected ? 'text-[#ea7500]' : 'text-gray-700'}`}>
                      {sub.topics}
                    </span>
                    <Badge variant={diffMap[sub.difficulty] || 'neutral'} size="sm">
                      {sub.difficulty}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Type Config Panel (validation rules display) ────────────────────────────

function TypeConfigPanel({ config, mcqFormat }: { config: any; mcqFormat?: string }) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['charLimits']));

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isOpen = (id: string) => expandedSections.has(id);

  const isNonAnbima = mcqFormat === 'non-anbima';

  // Extract data
  const type = config.type || {};
  const charLimits = config.charLimits || {};
  const validation = config.validation || {};
  const globalRules = config.globalRules || {};
  const dominios = config.dominiosCognitivos || [];
  const distratores = config.distratoresHierarquia || {};
  const structureTemplate = config.structureTemplate || {};

  // Non-ANBIMA overrides for char validation ranges
  const effectiveCharValidation = isNonAnbima && validation.charValidation
    ? {
        ...validation.charValidation,
        contextoComando_semEspaco: { min: 30, target: 300, max: 500, unit: 'chars_no_space' },
        alternativaTexto_semEspaco: { min: 20, target: 100, max: 150, unit: 'chars_no_space' },
      }
    : validation.charValidation;

  // Non-ANBIMA overrides for display char limits
  const effectiveCharLimits = isNonAnbima
    ? {
        contextoComando: {
          semEspaco: { aproximado: 300, maximo: 500, descricao: 'Non-ANBIMA: 30–500 caracteres sem espaços no contexto+comando' },
          comEspaco: { estimado_min: 35, estimado_max: 580 },
        },
        alternativasTotal: {
          semEspaco: { aproximado: 400, maximo: 600 },
          comEspaco: { estimado_min: 80, estimado_max: 700 },
          perAlternativa: { semEspaco_medio: 125 },
        },
      }
    : charLimits;
  const structure = config.structure || {};

  return (
    <div className="mt-4 space-y-3">
      {/* Type header */}
      <div className="flex items-center gap-3 px-1">
        <Info className="w-4 h-4 text-[#1e73be]" />
        <span className="text-sm font-bold text-gray-900">{type.name || type.abbreviation}</span>
        <span className="text-xs text-gray-400">{type.nameEn}</span>
      </div>
      <p className="text-xs text-gray-500 px-1">{type.description}</p>

      {/* ── Character Limits ── */}
      <CollapsibleSection
        id="charLimits"
        icon={Ruler}
        title={isNonAnbima ? 'Character Limits (Non-ANBIMA)' : 'Character Limits'}
        isOpen={isOpen('charLimits')}
        onToggle={() => toggleSection('charLimits')}
      >
        {isNonAnbima && (
          <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 font-medium">Non-ANBIMA format: shorter stems (30–500 chars), per-alternative limit (20–150 chars)</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(effectiveCharLimits).map(([key, val]: [string, any]) => {
            if (!val || typeof val !== 'object') return null;
            const semEspaco = val.semEspaco || val;
            const comEspaco = val.comEspaco;
            return (
              <div key={key} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1.5">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase())}
                </p>
                {semEspaco?.aproximado != null && (
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Sem espaço</span>
                    <span className="font-bold text-[#1e73be]">~{semEspaco.aproximado} chars</span>
                  </div>
                )}
                {semEspaco?.maximo != null && (
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Sem espaço (max)</span>
                    <span className="font-bold text-[#1e73be]">{semEspaco.maximo} chars</span>
                  </div>
                )}
                {comEspaco?.estimado_min != null && (
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Com espaço</span>
                    <span className="font-medium text-gray-600">
                      {comEspaco.estimado_min}–{comEspaco.estimado_max}
                    </span>
                  </div>
                )}
                {val.perAlternativa && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Per alternativa</span>
                    <span className="font-medium text-gray-600">~{val.perAlternativa.semEspaco_medio}</span>
                  </div>
                )}
                {semEspaco?.descricao && (
                  <p className="text-[10px] text-gray-400 mt-1">{semEspaco.descricao}</p>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* ── Structure Validation ── */}
      {validation.structureValidation && (
        <CollapsibleSection
          id="structure"
          icon={ShieldCheck}
          title="Structure Validation"
          isOpen={isOpen('structure')}
          onToggle={() => toggleSection('structure')}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(validation.structureValidation).map(([key, val]: [string, any]) => (
              <div key={key} className="bg-gray-50 rounded-xl px-3 py-2">
                <p className="text-[10px] text-gray-400 mb-0.5">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase())}
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {typeof val === 'object' ? (val.exact ?? JSON.stringify(val)) : String(val)}
                </p>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Char Validation Ranges ── */}
      {effectiveCharValidation && (
        <CollapsibleSection
          id="charValidation"
          icon={Ruler}
          title={isNonAnbima ? 'Char Validation Ranges — Non-ANBIMA (min / target / max)' : 'Char Validation Ranges (min / target / max)'}
          isOpen={isOpen('charValidation')}
          onToggle={() => toggleSection('charValidation')}
        >
          <div className="space-y-2">
            {Object.entries(effectiveCharValidation).map(([key, val]: [string, any]) => (
              <div key={key} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2">
                <span className="text-xs text-gray-600 font-medium flex-1 min-w-0 truncate">{key}</span>
                <div className="flex items-center gap-2 text-xs font-mono flex-shrink-0">
                  <span className="text-red-400">{val.min}</span>
                  <span className="text-gray-300">/</span>
                  <span className="text-emerald-600 font-bold">{val.target}</span>
                  <span className="text-gray-300">/</span>
                  <span className="text-red-400">{val.max}</span>
                </div>
                <span className="text-[10px] text-gray-400">{val.unit?.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Forbidden Terms (hidden for non-ANBIMA) ── */}
      {!isNonAnbima && globalRules.forbidden?.alternativas && (
        <CollapsibleSection
          id="forbidden"
          icon={Ban}
          title="Forbidden Terms"
          isOpen={isOpen('forbidden')}
          onToggle={() => toggleSection('forbidden')}
        >
          <div className="space-y-3">
            {Object.entries(globalRules.forbidden.alternativas).map(([key, val]: [string, any]) => (
              <div key={key} className="bg-red-50/50 border border-red-100 rounded-xl p-3">
                <p className="text-xs font-semibold text-red-700 mb-1">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase())}
                </p>
                {val.palavras && (
                  <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                    {val.palavras.map((p: string) => (
                      <span key={p} className="px-2 py-0.5 rounded bg-red-100 text-[11px] text-red-600 font-medium">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {val.proibidas && (
                  <div className="space-y-1 mb-1.5">
                    {val.proibidas.map((p: string) => (
                      <p key={p} className="text-[11px] text-red-600 bg-red-100 px-2 py-0.5 rounded">"{p}"</p>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-red-500">{val.regra || val.motivo}</p>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Required Rules ── */}
      {globalRules.required && (
        <CollapsibleSection
          id="required"
          icon={ShieldCheck}
          title="Required Rules"
          isOpen={isOpen('required')}
          onToggle={() => toggleSection('required')}
        >
          <div className="space-y-3">
            {Object.entries(globalRules.required).map(([key, val]: [string, any]) => (
              <div key={key} className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-3">
                <p className="text-xs font-semibold text-emerald-700 mb-1.5 capitalize">{key}</p>
                {val.regras && (
                  <ul className="space-y-1">
                    {val.regras.map((r: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-emerald-700">
                        <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Cognitive Domains ── */}
      {dominios.length > 0 && (
        <CollapsibleSection
          id="dominios"
          icon={BookOpen}
          title="Cognitive Domains"
          isOpen={isOpen('dominios')}
          onToggle={() => toggleSection('dominios')}
        >
          <div className="space-y-2">
            {dominios.map((d: any) => (
              <div key={d.nivel} className="bg-purple-50/50 border border-purple-100 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-purple-700 capitalize">{d.nivel}</span>
                </div>
                <p className="text-[11px] text-purple-600 mb-1.5">{d.description}</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(d.verbos || []).map((v: string) => (
                    <span key={v} className="px-2 py-0.5 rounded bg-purple-100 text-[10px] text-purple-700 font-medium">
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Distractor Hierarchy ── */}
      {distratores.distrator1 && (
        <CollapsibleSection
          id="distratores"
          icon={AlertTriangle}
          title="Distractor Hierarchy"
          isOpen={isOpen('distratores')}
          onToggle={() => toggleSection('distratores')}
        >
          <p className="text-xs text-gray-500 mb-2">{distratores.description}</p>
          <div className="space-y-2">
            {['distrator1', 'distrator2', 'distrator3'].map((key) => {
              const d = distratores[key];
              if (!d) return null;
              return (
                <div key={key} className="flex items-start gap-3 bg-amber-50/50 border border-amber-100 rounded-xl p-3">
                  <span className="text-xs font-bold text-amber-700 w-20 flex-shrink-0 capitalize">
                    {key.replace('distrator', 'Distrator ')}
                  </span>
                  <div>
                    <p className="text-[11px] text-amber-700 font-medium">{d.nivel?.replace(/_/g, ' ')}</p>
                    <p className="text-[10px] text-amber-600">{d.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Structure Template ── */}
      {structureTemplate && Object.keys(structureTemplate).length > 0 && (
        <CollapsibleSection
          id="template"
          icon={FileText}
          title="Structure Template"
          isOpen={isOpen('template')}
          onToggle={() => toggleSection('template')}
        >
          <pre className="bg-gray-900 text-green-400 text-[11px] p-4 rounded-xl overflow-x-auto">
            {JSON.stringify(structureTemplate, null, 2)}
          </pre>
        </CollapsibleSection>
      )}

      {/* ── Component Structure ── */}
      {structure.components && (
        <CollapsibleSection
          id="components"
          icon={Layers}
          title="Question Components"
          isOpen={isOpen('components')}
          onToggle={() => toggleSection('components')}
        >
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {structure.components.map((c: string, i: number) => (
              <div key={c} className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-lg bg-[#1e73be]/10 text-[#1e73be] text-xs font-bold">
                  {c}
                </span>
                {i < structure.components.length - 1 && (
                  <span className="text-gray-300">→</span>
                )}
              </div>
            ))}
          </div>
          {Object.entries(structure)
            .filter(([key]) => key !== 'components')
            .map(([key, val]: [string, any]) => (
              <div key={key} className="bg-gray-50 rounded-xl p-3 mb-2">
                <p className="text-xs font-semibold text-gray-700 mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                {val.description && <p className="text-[11px] text-gray-500">{val.description}</p>}
                {val.position && <p className="text-[10px] text-gray-400 mt-0.5">Position: {val.position}</p>}
                {val.verbosRecomendados && (
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {val.verbosRecomendados.map((v: string) => (
                      <span key={v} className="px-1.5 py-0.5 rounded bg-blue-100 text-[10px] text-[#1e73be] font-medium">{v}</span>
                    ))}
                  </div>
                )}
                {val.quantidade != null && (
                  <p className="text-[11px] text-gray-600 mt-1">Quantity: <strong>{val.quantidade}</strong></p>
                )}
                {val.maxQuestoes != null && (
                  <p className="text-[11px] text-gray-600 mt-1">Max questions: <strong>{val.maxQuestoes}</strong></p>
                )}
                {val.mediaQuantidade != null && (
                  <p className="text-[11px] text-gray-600 mt-1">Average prompts: <strong>{val.mediaQuantidade}</strong></p>
                )}
              </div>
            ))}
        </CollapsibleSection>
      )}
    </div>
  );
}

function CollapsibleSection({
  icon: Icon,
  title,
  isOpen,
  onToggle,
  children,
}: {
  id?: string;
  icon: React.ElementType;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <Icon className="w-4 h-4 text-[#ea7500] flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-900 flex-1">{title}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default PdfQuestionGenerator;
